import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import dayjs from "dayjs"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Filters from "components/Common/Filters"
import { Link, useLocation, useNavigate } from "react-router-dom"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import { useSelector } from "react-redux"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { useDispatch } from "react-redux"
import {
  getAllOrders,
  syncOrderWithSalasa,
  updateOrderStatus,
} from "store/actions/orderAction"
import TableShimmer from "components/Shared/TableShimmer"
import { getAllBranchs } from "store/actions/usersAction"
import {
  buildOrderExportPayload,
  canExportOrders,
  downloadOrderExport,
} from "utils/orderExportUtils"
import { downloadOrderDetailPdf } from "helpers/orderDetailPdf"
import { hasWalletRelatedPermission } from "utils/walletPermissionUtils"
import {
  getPerPageOptions,
  statusListSalasaStatus,
  salasaOrderStatus,
  mobileOrderStatus,
} from "components/Common/constants"
import Select from "react-select"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import { setItemsPerPage } from "store/reducers/orderReducer"
import { getAppVersions } from "store/actions/customersAction"
import { removeOrderFromList } from "store/reducers/orderReducer"
import {
  getSystemStatusDisplay,
  getSystemStatusFilterOptions,
  getSalasaPosStatusFilterOptions,
  getPosStatusLabel,
  getPosStatusColor,
  resolvePosStatusValue,
  resolveSystemOrderStatusValue,
  getSystemOrderStatusLabel,
} from "utils/orderStatusUtils"
import { markOrderSentToSalasa } from "store/reducers/orderReducer"
import { useAssignedBrands } from "hooks/useAssignedBrands"

const formatDateTime = date =>
  date
    .toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "")

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const ORDER_STATUS_SELECT_MENU_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  menuPlacement: "auto",
  menuShouldScrollIntoView: false,
}

const orderStatusSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({ ...base, zIndex: 9999 }),
}

const B2cOrders = ({ brandConfig = null }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const ordersBasePath = brandConfig
    ? `/orders/${brandConfig.brandId}`
    : "/b2c-orders"
  const serviceProvider = brandConfig?.serviceProvider || "Salasa"
  // Orders.brandId is stored as string — keep filter payload as string
  const brandId =
    brandConfig?.brandId != null && String(brandConfig.brandId).trim() !== ""
      ? String(brandConfig.brandId).trim()
      : ""
  const { hasOnlyOneBrand } = useAssignedBrands()
  const pageTitle = hasOnlyOneBrand
    ? t("Orders")
    : brandConfig?.displayName ||
      brandConfig?.brandName ||
      t("B2C Orders")

  document.title = `${pageTitle} `

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const branchIdParam = searchParams.get("branchId")
  const orderTypeParam = searchParams.get("orderType")
  const platformParam = searchParams.get("platform")
  const minDateParam = searchParams.get("minDate")
  const maxDateParam = searchParams.get("maxDate")

  const {
    loading,
    isLoading,
    isUpdating,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    orders,
  } = useSelector(state => state.Orders)
  const { branchsList } = useSelector(state => state.Users)
  const { appVersions } = useSelector(state => state.Customers)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [searchTerm2, setSearchTerm2] = useState("")

  // const [appVersion, setAppVersion] = useState("")

  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    status: parseInt(searchParams.get("status") || "0"),
    posStatus: parseInt(searchParams.get("posStatus") || "0"),
    platform: platformParam
      ? platformParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    appVersion: "",
  })

  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState(() =>
    minDateParam && maxDateParam
      ? { min: minDateParam, max: maxDateParam }
      : getDefaultDateRange()
  )
  const [showModal, setShowModal] = useState(false)
  const [downloadingPdfId, setDownloadingPdfId] = useState(null)
  const [selectedOrderIds, setSelectedOrderIds] = useState([])

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "status",
      type: "select",
      options: getSystemStatusFilterOptions(t),
      placeholder: t("Select Order Status"),
    },
    {
      name: "posStatus",
      type: "select",
      options: getSalasaPosStatusFilterOptions(t),
      placeholder: t("POS Status"),
    },
    {
      name: "platform",
      type: "multiSelect",
      options: [
        { label: t("Android"), value: "android" },
        { label: t("iOS"), value: "iOS" },
        { label: t("Website"), value: "website" },
      ],
      placeholder: t("Platform"),
    },
  ]

  if (
    selectedFilters?.platform?.some(
      item => item.value === "android" || item.value === "iOS"
    )
  ) {
    filters.push({
      name: "appVersion",
      type: "select",
      options: appVersions.map(item => ({
        label: item,
        value: item,
      })),
      placeholder: t("App Versions"),
    })
  }

  useEffect(() => {
    fetchAllOrders()
    fetchAllBranchs()
  }, [])

  const fetchAllOrders = () => {
    const { status, posStatus, sortOrder, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform =
      platform?.flatMap(item => {
        if (item.value === "android") return [item.value, "Android"]
        if (item.value === "website") return [item.value, "Website"]
        return item.value
      }) || []
    const posVal = Number(posStatus) || 0
    // Special Salasa filters 9/10 still use status field on backend
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      orderNo: searchTerm2 || "",
      appVersion: platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
        ? appVersion
        : "",
      status: posVal === 9 || posVal === 10 ? posVal : status,
      posStatus:
        posVal > 0 && posVal < 9 ? posVal : undefined,
      branchId: branchIds,
      orderType: updatedOrderType,
      platform: updatedPlatform,
      serviceProvider,
      brandId,
      dateRange:
        dateRange?.min && dateRange?.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllOrders(payload))
    setIsApiCalled(true)
  }

  useEffect(() => {
    const { platform } = selectedFilters
    if (
      platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
    ) {
      fetchAppVersions()
    }
  }, [selectedFilters])

  const fetchAppVersions = () => {
    const { platform } = selectedFilters
    const updatedPlatform =
      platform
        ?.filter(item => item.value === "android" || item.value === "iOS")
        .map(item => item.value) || []
    const transformedPlatform = updatedPlatform?.map(item =>
      item === "android" ? "Android" : item
    )
    const payload = { platform: transformedPlatform }
    dispatch(getAppVersions(payload))
  }

  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
    export: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = getSubPermissions(
      userRole,
      9,
      "order_1",
      accountType
    )
    const isPermissionEdit = getSubPermissions(
      userRole,
      9,
      "order_2",
      accountType
    )
    const canViewOrders = isPermissionEnabled(
      userRole,
      9,
      "order_1",
      accountType
    )
    if (!canViewOrders) {
      navigate("/dashboard")
      return
    }
    setUsersPermissions({
      list: canViewOrders,
      edit:
        isPermissionEnabled(userRole, 9, "order_2", accountType) ||
        isPermissionEdit?.update_orders?.isEnabled,
      export: canExportOrders({
        userRole,
        accountType,
      }),
    })
  }, [userRole])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchChange2 = e => {
    setSearchTerm2(e.target.value)
  }

  const handleAppVersionChange = e => {
    // setAppVersion(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: selectedValue,
      }

      if (filterName === "platform" && selectedValue.length === 0) {
        updatedFilters.appVersion = "" // Reset appVersion if platform is cleared
      }

      return updatedFilters
    })
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const handleFetchOrders = (page = 1) => {
    const { status, sortOrder, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    // const updatedPlatform = platform?.map(item => item.value) || []
    const updatedPlatform =
      platform?.flatMap(item => {
        if (item.value === "android") return [item.value, "Android"]
        if (item.value === "website") return [item.value, "Website"]
        return item.value
      }) || []

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (branchId?.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("branchId", branchIdString)
    }
    if (orderType?.length > 0) {
      const orderTypeString = orderType
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("orderType", orderTypeString)
    }
    if (platform?.length > 0) {
      const platformString = platform
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("platform", platformString)
    }

    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      orderNo: searchTerm2 || "",
      appVersion: platform?.some(
        item => item.value === "android" || item.value === "iOS"
      )
        ? appVersion
        : "",
      branchId: branchIds,
      status: Number(status),
      orderType: updatedOrderType,
      platform: updatedPlatform,
      serviceProvider,
      brandId,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getAllOrders(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`${ordersBasePath}?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchOrders(1)
  }

  const onPageChange = page => {
    handleFetchOrders(page)
  }

  // useEffect(() => {
  //   const { platform } = selectedFilters
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters, itemsPerPage])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleDelete = () => {
    setShowDeleteModal(false)
  }

  const handleReset = () => {
    dispatch(setItemsPerPage(50))

    const resetDateRange = getDefaultDateRange()
    const payload = {
      perPage: 50,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      status: 0,
      dateRange: resetDateRange,
      serviceProvider,
      brandId,
    }
    dispatch(getAllOrders(payload))
    setSelectedFilters({
      sortOrder: 2,
      branchId: [],
      status: 0,
      orderType: [],
      platform: [],
      appVersion: "",
      serviceProvider,
    })
    // setAppVersion("")
    setSearchTerm("")
    setSearchTerm2("")
    setSelectedOrderIds([])
    setDateRange(resetDateRange)
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate(
        [resetDateRange.min, resetDateRange.max],
        true
      )
    }
    navigate(`${ordersBasePath}?page=1`)
  }

  // const formatPromiseDateTime = dateTimeString => {
  //   if (!dateTimeString) return "-"

  //   const date = new Date(dateTimeString)

  //   // Convert UTC to KSA by adding 3 hours
  //   const KSA_OFFSET_MS = 3 * 60 * 60 * 1000 // 3 hours in milliseconds
  //   const ksaDate = new Date(date.getTime() + KSA_OFFSET_MS)

  //   const year = ksaDate.getUTCFullYear()
  //   const month = String(ksaDate.getUTCMonth() + 1).padStart(2, "0")
  //   const day = String(ksaDate.getUTCDate()).padStart(2, "0")
  //   let hours = ksaDate.getUTCHours()
  //   const meridiem = hours >= 12 ? "PM" : "AM"
  //   hours = hours % 12 || 12 // Convert 0 to 12 for 12-hour clock
  //   const minutes = String(ksaDate.getUTCMinutes()).padStart(2, "0")
  //   const seconds = String(ksaDate.getUTCSeconds()).padStart(2, "0")

  //   return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${meridiem}`
  // }

  const formatPromiseDateTime = dateTimeString => {
    if (!dateTimeString) return "-"
    return dayjs
      .utc(dateTimeString)
      .tz("Asia/Riyadh")
      .format("YYYY-MM-DD HH:mm:ss")
    // if (!dateTimeString) return "-"
    // const date = new Date(dateTimeString)
    // const year = date.getFullYear()
    // const month = String(date.getMonth() + 1).padStart(2, "0")
    // const day = String(date.getDate()).padStart(2, "0")
    // let hours = date.getHours()
    // const meridiem = hours >= 12 ? "PM" : "AM"
    // hours = hours % 12 || 12 // Convert 0 to 12 for 12-hour clock
    // const minutes = String(date.getMinutes()).padStart(2, "0")
    // const seconds = String(date.getSeconds()).padStart(2, "0")

    // return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${meridiem}`
  }

  const getOrderId = order => String(order?._id || order?.id || "")

  const selectedOrders = orders.filter(o =>
    selectedOrderIds.includes(getOrderId(o))
  )
  const hasSelection = selectedOrderIds.length > 0

  const toggleOrderSelection = (orderId, e) => {
    e?.stopPropagation()
    const id = String(orderId)
    if (!id) return
    setSelectedOrderIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAllOnPage = e => {
    e?.stopPropagation()
    const pageIds = orders.map(o => getOrderId(o)).filter(Boolean)
    const allSelected = pageIds.every(id => selectedOrderIds.includes(id))
    if (allSelected) {
      setSelectedOrderIds(prev => prev.filter(id => !pageIds.includes(id)))
    } else {
      setSelectedOrderIds(prev => [...new Set([...prev, ...pageIds])])
    }
  }

  const isAllOnPageSelected =
    orders.length > 0 &&
    orders.every(o => selectedOrderIds.includes(getOrderId(o)))

  const getExportFileName = (suffix = "") => {
    const hasDateRange = dateRange?.min && dateRange?.max
    if (selectedOrderIds.length > 0) {
      return t("Selected B2C Orders{{suffix}}", {
        suffix: suffix ? ` (${suffix.toUpperCase()})` : "",
      })
    }
    if (hasDateRange) {
      return t(`b2c_order_from_${dateRange.min}_to_${dateRange.max}`)
    }
    return t("B2C Orders Till Date")
  }

  const runOrderExport = async exportType => {
    const payload = buildOrderExportPayload({
      exportType,
      selectedFilters,
      searchTerm,
      searchTerm2,
      dateRange,
      serviceProvider,
      includeServiceProvider: true,
      includePosColumns: true,
      includeSalasaColumns: true,
      brandId,
      orderIds: selectedOrderIds,
    })

    await downloadOrderExport({
      dispatch,
      t,
      payload,
      fileName: getExportFileName(exportType === "xlsx" ? "xlsx" : "csv"),
    })
  }

  const handleExcelDownload = async () => {
    await runOrderExport("xlsx")
  }

  const exportToCSV = async () => {
    await runOrderExport("csv")
  }

  const handleExportSelected = async exportType => {
    if (!selectedOrderIds.length) return
    await runOrderExport(exportType)
  }

  const handleRowClick = id => {
    navigate(`${ordersBasePath}/order-details?id=${id}`)
  }

  // Convert bookingStatus to options array for react-select
  const statusOptionsSalasa = Object.entries(salasaOrderStatus).map(
    ([value, label]) => ({
      value,
      label: t(label),
    })
  )

  const handleStatusChange = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status,
    }
    dispatch(updateOrderStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setSelectedId(null)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleSentOnSalasa = () => {
    const payload = {
      orderId: selectedId,
    }
    dispatch(syncOrderWithSalasa(payload))
      .then(action => {
        console.log("actions is: ", action)
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          if (selectedId) {
            dispatch(markOrderSentToSalasa(selectedId))
          }
          //   if (selectedFilters?.status === 9 && selectedId) {
          //     dispatch(removeOrderFromList(selectedId))
          //   }
          setSelectedId(null)
          setShowModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
          setSelectedId(null)
          handleFetchOrders()
          setShowModal(false)
        }
      })
      .catch(error => {})
  }

  const perPageOptions = getPerPageOptions(totalCount)

  const handlePerPageChange = e => {
    dispatch(setItemsPerPage(Number(e.target.value)))
  }

  const handleDownloadOrderPdf = async (orderId, e) => {
    e?.stopPropagation()
    e?.preventDefault()
    if (!orderId || downloadingPdfId) return
    setDownloadingPdfId(String(orderId))
    try {
      await downloadOrderDetailPdf(orderId, {
        includeWalletAmount: hasWalletRelatedPermission(
          userRole,
          accountType,
          accountScope
        ),
      })
    } catch (error) {
      dispatch(
        showAlert({
          text: error?.message || t("Download failed."),
          type: "error",
        })
      )
    } finally {
      setDownloadingPdfId(null)
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                cardTitle={pageTitle}
                isAddBtn={false}
                isExportData={loading ? false : usersPermissions.export}
                isCustomPage={true}
                perPageOptions={perPageOptions}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  dateRangeWithTime={true}
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  // isSecondSearch={false}
                  // appVersion={appVersion}
                  // handleAppVersionChange={handleAppVersionChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  pageSizes={[0, 1, 2]}
                  itemsPerPage={50}
                  setItemsPerPage={null}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Name, Phone number")}
                  isSecondSearch={true}
                  searchTerm2={searchTerm2}
                  handleSearchChange2={handleSearchChange2}
                  searchBy2={t("Order No.")}
                  colSize={4}
                  loading={loading}
                />
                {hasSelection && usersPermissions.export && (
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 bg-light rounded">
                    <span className="text-muted me-2">
                      {t("{{count}} selected", {
                        count: selectedOrderIds.length,
                      })}
                    </span>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handleExportSelected("xlsx")}
                    >
                      {t("Export Selected (Excel)")}
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => handleExportSelected("csv")}
                    >
                      {t("Export Selected (CSV)")}
                    </Button>
                    <Button
                      color="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => setSelectedOrderIds([])}
                    >
                      {t("Clear selection")}
                    </Button>
                  </div>
                )}
                {/* )} */}
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : orders.length === 0 ? (
                    <div className="g_center" style={{ minHeight: 200 }}>
                      <p className="text-center my-2 mb-2">
                        {t("No orders found")}
                      </p>
                    </div>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {usersPermissions.export && (
                            <th
                              style={{ width: 40 }}
                              onClick={e => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isAllOnPageSelected}
                                onChange={toggleSelectAllOnPage}
                                onClick={e => e.stopPropagation()}
                              />
                            </th>
                          )}
                          <th style={{ whiteSpace: "nowrap" }}>
                            {t("S. No.")}
                          </th>
                          <th>{t("Order No")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Number")}</th>
                          {/* <th>Email</th> */}
                          <th>{t("Platform")}</th>
                          <th>{t("Amount")}</th>
                          <th>{t("Order Type")}</th>
                          <th>{t("Service Provider")}</th>
                          <th>{t("Date & Time")}</th>
                          <th>{t("Payment Method")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("POS Status")}</th>
                          {usersPermissions.edit && (
                            <th style={{ minWidth: 200 }}>
                              {t("Update Status")}
                            </th>
                          )}
                          <th>{t("Salasa Response")}</th>
                          <th style={{ width: 56, textAlign: "center" }}>
                            {t("Download")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              handleRowClick(item._id)
                            }}
                            className="cursor-pointer"
                          >
                            {usersPermissions.export && (
                              <td onClick={e => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedOrderIds.includes(
                                    getOrderId(item)
                                  )}
                                  onChange={e =>
                                    toggleOrderSelection(getOrderId(item), e)
                                  }
                                  onClick={e => e.stopPropagation()}
                                />
                              </td>
                            )}
                            <th>
                              {" "}
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            {/* <th scope="row">
                              <Link
                                className="text-decoration-underline gray_text"
                                to={`/orders/order-details?id=${item._id}`}
                              >
                                {item._id}
                              </Link>
                            </th> */}
                            <td>{item.invoiceNo}</td>
                            <td>{item?.name || "-"}</td>
                            <td>
                              {item?.phone ? (
                                <span
                                  dir="ltr"
                                  style={{ unicodeBidi: "bidi-override" }}
                                >
                                  {item.phone}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>{" "}
                            {/* <td>{item.email || "-"}</td> */}
                            <td key={language}>
                              {item.platform === "android" ||
                              item.platform === "Android"
                                ? t("Android")
                                : item.platform === "iOS"
                                ? t("iOS")
                                : item.platform === "website" ||
                                  item.platform === "Website"
                                ? t("Website")
                                : item.platform
                                ? t(item.platform)
                                : "-"}
                              {(item.platform === "android" ||
                                item.platform === "Android" ||
                                item.platform === "iOS") &&
                                item?.appVersion &&
                                ` (${item.appVersion})`}
                            </td>
                            <td>
                              {item.paidAmount} {item?.splitPayments?.currency}
                            </td>
                            <td className="text-capitalize">
                              {t(item.orderType)}
                            </td>
                            <td className="text-capitalize">
                              {t(item.serviceProvider)}
                            </td>
                            <td>{formatPromiseDateTime(item.createdAt)}</td>
                            <td>
                              <i
                                className={`${
                                  item.paymentMethod === "CARD"
                                    ? "fab fa-cc-mastercard me-1"
                                    : "" || item.paymentMethod === "APPLE"
                                    ? "fab bx bxl-apple me-1"
                                    : ""
                                }`}
                              ></i>
                              {t(item.paymentMethod)}
                            </td>
                            <td
                            // style={{
                            //   whiteSpace: "nowrap",
                            //   verticalAlign: "middle",
                            // }}
                            >
                              {/* SHOW SEND TO BRANCH FAILED IF HAVE POS_STATUS = ERROR */}
                              <span
                                style={{
                                  color: getSystemStatusDisplay(
                                    { ...item, brandIntegration: "Salasa" },
                                    t
                                  ).color,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {
                                  getSystemStatusDisplay(
                                    { ...item, brandIntegration: "Salasa" },
                                    t
                                  ).text
                                }
                              </span>
                            </td>
                            <td>
                              <span
                                style={{
                                  color:
                                    statusListSalasaStatus[
                                      item?.orderStatus === 1 &&
                                      item?.paymentStatus === 1 &&
                                      item?.salasaOrderId === null &&
                                      item?.sentToSalasaFlag
                                        ? 6
                                        : resolvePosStatusValue({
                                            ...item,
                                            brandIntegration: "Salasa",
                                          })
                                    ]?.color,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item?.orderStatus === 1 &&
                                item?.paymentStatus === 1 &&
                                item?.salasaOrderId === null &&
                                item?.sentToSalasaFlag
                                  ? t("Sent To Salasa Failed")
                                  : getPosStatusLabel(
                                      { ...item, brandIntegration: "Salasa" },
                                      t
                                    )}
                              </span>
                            </td>
                            {/* <td>
                              {usersPermissions.list && (
                                <Button
                                  type="button"
                                  color="primary"
                                  className="btn-sm btn-rounded"
                                  onClick={() => {
                                    navigate(
                                      `/orders/order-details?id=${item._id}`
                                    )
                                  }}
                                >
                                  View Details
                                </Button>
                              )}
                            </td> */}
                            {usersPermissions.edit && (
                              <th onClick={e => e.stopPropagation()}>
                                {item?.orderStatus > 0 &&
                                item?.paymentStatus === 1 &&
                                item?.salasaOrderId === null ? (
                                  <Button
                                    color="primary"
                                    title={t("Sync Order With Salasa")}
                                    disabled={!!item?.sentToSalasaFlag}
                                    onClick={() => {
                                      setShowModal(true)
                                      setSelectedId(item._id)
                                    }}
                                  >
                                    {t("Send on Salasa")}
                                  </Button>
                                ) : (
                                  <Select
                                    value={{
                                      value: resolveSystemOrderStatusValue(
                                        item
                                      ).toString(),
                                      label: getSystemOrderStatusLabel(
                                        resolveSystemOrderStatusValue(item),
                                        item.orderType,
                                        t
                                      ),
                                    }}
                                    isDisabled={
                                      selectedId === item._id && isLoading
                                    }
                                    onChange={(selectedOption, event) => {
                                      if (event.nativeEvent) {
                                        event.nativeEvent.stopPropagation()
                                      }
                                      handleStatusChange(
                                        item._id,
                                        selectedOption.value
                                      )
                                    }}
                                    options={Object.entries(mobileOrderStatus)
                                      .map(([value]) => ({
                                        value,
                                        label: getSystemOrderStatusLabel(
                                          value,
                                          item.orderType,
                                          t
                                        ),
                                      }))
                                      .filter(option => {
                                        const currentStatus =
                                          resolveSystemOrderStatusValue(item)
                                        const optionValue = parseInt(
                                          option.value
                                        )
                                        if (currentStatus < 5) {
                                          return optionValue === 5
                                        }
                                        return false
                                      })
                                      .map(option => ({
                                        ...option,
                                        isDisabled:
                                          parseInt(option.value) ===
                                          resolveSystemOrderStatusValue(item),
                                      }))}
                                    placeholder={t("Update Status")}
                                    noOptionsMessage={() => t("No options")}
                                    {...ORDER_STATUS_SELECT_MENU_PROPS}
                                    styles={orderStatusSelectStyles}
                                  />
                                )}
                              </th>
                            )}
                            <td className="text-capitalize">
                              {t(
                                item?.salasaResponseMessage
                                  ? item?.salasaResponseMessage
                                  : item?.orderStatus === 1 &&
                                    item?.paymentStatus === 1 &&
                                    item.salasaOrderId != null
                                  ? "Order has been placed."
                                  : "-"
                              )}
                            </td>
                            <td
                              style={{ textAlign: "center" }}
                              onClick={e => e.stopPropagation()}
                              onMouseDown={e => e.stopPropagation()}
                            >
                              <i
                                id={`download_order_pdf_${item._id}`}
                                className={`mdi ${
                                  downloadingPdfId === String(item._id)
                                    ? "mdi-loading mdi-spin"
                                    : "mdi-download"
                                } font-size-18 text-primary cursor-pointer`}
                                onClick={e =>
                                  handleDownloadOrderPdf(item._id, e)
                                }
                              ></i>
                              <UncontrolledTooltip
                                placement="top"
                                target={`download_order_pdf_${item._id}`}
                              >
                                {t("Download Order Details PDF")}
                              </UncontrolledTooltip>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && orders.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={orders}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <Modal size="md" isOpen={showModal} centered={true}>
        <div className="modal-content">
          <ModalBody className="px-4 py-5 text-center">
            <button
              type="button"
              onClick={() => {
                setShowModal(false)
                setSelectedId(null)
              }}
              className="btn-close position-absolute end-0 top-0 m-3"
            ></button>
            <div className="avatar-sm mb-4 mx-auto">
              <div className="avatar-title bg-primary text-primary bg-opacity-10 font-size-20 rounded-3">
                <i className="bx bx-sync"></i>
              </div>
            </div>
            <p className="text-muted font-size-16 mb-4">
              {t("Are you sure you want to send this order to Salasa")}
            </p>

            <div className="hstack gap-2 justify-content-center mb-0">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false)
                  setSelectedId(null)
                }}
              >
                {t("No")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSentOnSalasa}
                disabled={isLoading}
                title={t("Sync Order With POS")}
              >
                {t("Yes")}
              </button>
            </div>
          </ModalBody>
        </div>
      </Modal>
    </div>
  )
}

export default B2cOrders
